import { CheckmarkOutlined } from '@icon';
import classNames from 'classnames';
import type { MenuItemProps as RcMenuItemProps } from 'rc-menu';
import { Item } from 'rc-menu';
import Tooltip, { TooltipPlacement } from '../../tooltip';
import React from 'react';
import { useTheme } from 'styled-components';
import { Checkbox } from '../../inputs';
import { useMenuContext } from '../menu.context';

export interface MenuItemProps extends Omit<RcMenuItemProps, 'title'> {
  Icon?: (p: React.SVGProps<SVGSVGElement>) => JSX.Element;
  variant?: 'default' | 'danger' | 'success';
  tooltip?: React.ReactNode;
  tooltipPlacement?: TooltipPlacement;
}

const MenuItem = (props: MenuItemProps) => {
  const {
    Icon,
    children,
    className,
    variant = 'default',
    tooltip,
    tooltipPlacement = 'bottomLeft',
    ...rest
  } = props;
  const theme = useTheme();
  const { selectMode, selectedKeys } = useMenuContext();
  const isSelected = selectedKeys?.includes(props.eventKey!);

  const content = tooltip ? (
    <Tooltip
      content={tooltip}
      trigger={['hover']}
      placement={tooltipPlacement}
      mouseEnterDelay={0.5}
    >
      <div className="ui-menu-item-children">{children}</div>
    </Tooltip>
  ) : (
    <div className="ui-menu-item-children">{children}</div>
  );

  return (
    <Item {...rest} className={classNames('ui-menu-item', className, `ui-menu-item-${variant}`)}>
      <div className="ui-menu-item-inner">
        {Icon ? (
          <Icon
            className="ui-menu-item-icon"
            width={24}
            height={24}
            color={
              variant === 'danger'
                ? theme.color.tint.error
                : variant === 'success'
                ? theme.color.tint.success
                : theme.color.tint.tertiary
            }
          />
        ) : null}
        {content}
        {selectMode === 'single' && isSelected && (
          <div className="ui-menu-item-check">
            <CheckmarkOutlined color={theme.color.tint.selection} />
          </div>
        )}
        {selectMode === 'multi' && (
          <div className="ui-menu-item-checkbox">
            <Checkbox value={props.eventKey} checked={isSelected} />
          </div>
        )}
      </div>
    </Item>
  );
};

export default MenuItem;
